import { useState, useEffect } from 'react'
import { inviteService } from '../services/inviteService'
import { profileService } from '@shoots/auth'
import type { Profile } from '@shoots/auth'
import type { Invite } from '../types'

interface UseInviteProfileResult {
  invite: Invite | null
  profile: Profile | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  updateInviteResponse: (response: 'yes' | 'no') => void
}

export function useInviteProfile(
  inviteId?: string,
  authenticated?: boolean
): UseInviteProfileResult {
  const [invite, setInvite] = useState<Invite | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchInviteAndProfile = async (id: string) => {
    setLoading(true)
    setError(null)

    try {
      // First, fetch the invite information
      const inviteData = await inviteService.getInvite(id)
      setInvite(inviteData)

      // Then, fetch the profile information using the profile_id from the invite
      // For unauthenticated users, profile_id is not returned (prevents enumeration)
      if (inviteData.profile_id) {
        const profileData = await profileService.getProfile(
          inviteData.profile_id
        )
        setProfile(profileData)
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to fetch invite or profile data'
      setError(errorMessage)
      console.error('Error fetching invite/profile:', err)
    } finally {
      setLoading(false)
    }
  }

  const refetch = async () => {
    if (inviteId) {
      await fetchInviteAndProfile(inviteId)
    }
  }

  const updateInviteResponse = (response: 'yes' | 'no') => {
    setInvite(prev => (prev ? { ...prev, response } : null))
  }

  useEffect(() => {
    if (!inviteId) {
      setInvite(null)
      setProfile(null)
      setError(null)
      return
    }

    fetchInviteAndProfile(inviteId)
    // Refetch when authenticated changes so the server can include
    // profile_id (only returned to authenticated callers). Without this,
    // a page refresh races: the first fetch happens before the auth token
    // hydrates, the server omits profile_id, and we lose the signal that
    // the current user owns this invite.
  }, [inviteId, authenticated])

  return {
    invite,
    profile,
    loading,
    error,
    refetch,
    updateInviteResponse,
  }
}
